import React, { useState, useContext, useCallback, memo, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { showNotification } from '../utils/notification';
import { preloadImage, getImagePlaceholder } from '../utils/imageOptimizer';
import DietaryLabel from './DietaryLabel.jsx';

// Create a context to manage which card is expanded
const ExpandedCardContext = React.createContext();

export const ExpandedCardProvider = ({ children }) => {
  const [expandedCardId, setExpandedCardId] = useState(null);

  return (
    <ExpandedCardContext.Provider value={{ expandedCardId, setExpandedCardId }}>
      {children}
    </ExpandedCardContext.Provider>
  );
};

export const useExpandedCard = () => {
  const context = useContext(ExpandedCardContext);
  if (!context) {
    throw new Error('useExpandedCard must be used within an ExpandedCardProvider');
  }
  return context;
};

const MenuCard = memo(({ item }) => {
  const [selectedWeight, setSelectedWeight] = useState(item.hasWeightOptions ? item.weightOptions[0] : item.singlePrice);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState('next');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { addToCart } = useCart();
  const { expandedCardId, setExpandedCardId } = useExpandedCard();

  const slideImages = (() => {
    const combined = [
      ...(Array.isArray(item.imageUrls) ? item.imageUrls : []),
      item.imageUrl
    ].filter(Boolean);
    return Array.from(new Set(combined));
  })();

  const isCarousel = slideImages.length > 1;

  // Auto-advance carousel similar to HomeSection
  useEffect(() => {
    if (!slideImages || slideImages.length === 0) return;
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setSlideDirection('next');
        setCurrentSlide((prev) => (prev + 1) % slideImages.length);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [isTransitioning, slideImages.length]);

  // Preload current slide image
  useEffect(() => {
    const activeUrl = slideImages[currentSlide];
    if (!activeUrl) return;
    const loadImage = async () => {
      try {
        await preloadImage(activeUrl);
        setImageLoaded(true);
        setImageError(false);
      } catch (error) {
        console.warn('Failed to load image:', activeUrl);
        setImageError(true);
      }
    };
    loadImage();
  }, [currentSlide, slideImages]);

  const nextSlide = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!isTransitioning && slideImages.length > 1) {
      setIsTransitioning(true);
      setSlideDirection('next');
      setCurrentSlide((prev) => (prev + 1) % slideImages.length);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  }, [isTransitioning, slideImages.length]);

  const prevSlide = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!isTransitioning && slideImages.length > 1) {
      setIsTransitioning(true);
      setSlideDirection('prev');
      setCurrentSlide((prev) => (prev - 1 + slideImages.length) % slideImages.length);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  }, [isTransitioning, slideImages.length]);

  const goToSlide = useCallback((e, index) => {
    if (e) e.stopPropagation();
    if (!isTransitioning && index !== currentSlide) {
      setIsTransitioning(true);
      setSlideDirection(index > currentSlide ? 'next' : 'prev');
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 600);
    }
  }, [currentSlide, isTransitioning]);

  const handleWeightSelection = useCallback((weightOption) => {
    setSelectedWeight(weightOption);
  }, []);

  const handleAddToCart = useCallback(() => {
    let price, details;
    
    if (item.hasWeightOptions && selectedWeight) {
      price = selectedWeight.price;
      details = selectedWeight.weight;
    } else if (item.singlePrice) {
      price = item.singlePrice;
      details = '';
    } else {
      price = item.price || '0';
      details = '';
    }

    const cartItem = {
      name: item.name,
      price: price,
      details: details,
      image: (slideImages && slideImages.length > 0 ? slideImages[0] : item.imageUrl)
    };

    addToCart(cartItem);
    
    const weightText = details ? ` (${details})` : '';
    showNotification(`${item.name}${weightText} added to cart!`, 'success');
  }, [addToCart, item.name, item.price, item.singlePrice, slideImages, item.imageUrl, selectedWeight]);

  const handleCardClick = useCallback(() => {
    if (expandedCardId === item.id) {
      setExpandedCardId(null);
    } else {
      setExpandedCardId(item.id);
    }
  }, [expandedCardId, item.id, setExpandedCardId]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setImageError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(false);
  }, []);

  // Determine current price
  let currentPrice;
  if (item.hasWeightOptions && selectedWeight) {
    currentPrice = selectedWeight.price;
  } else if (item.singlePrice.length === 0) {
    currentPrice = item.singlePrice;
  } else {
    currentPrice = item.singlePrice || '0';
  }

  const isExpanded = expandedCardId === item.id;

  return (
    <div 
      className={`menu-card bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border border-[#F8E8D2] transition-all duration-300 ease-out cursor-pointer flex flex-col ${
        isExpanded 
          ? 'scale-105 -translate-y-2 z-10' 
          : 'hover:-translate-y-1 hover:shadow-xl group'
      }`}
      onClick={handleCardClick}
    >
      <div className="relative h-88 md:h-66 lg:h-70 overflow-hidden ">
        {!imageError && !imageLoaded && !isCarousel && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
          </div>
        )}
        {imageError && (
          <div className="absolute inset-0 bg-gray-200 flex items-center justify-center">
            <i className="fas fa-image text-gray-400 text-2xl"></i>
          </div>
        )}
        <div className="aspect-square overflow-hidden relative">
          {slideImages && slideImages.length > 0 ? (
            <div className="carousel-container w-full h-full relative overflow-hidden">
              {slideImages.map((url, index) => {
                const isActive = index === currentSlide;
                const isPrev = index === (currentSlide - 1 + slideImages.length) % slideImages.length;
                const isNext = index === (currentSlide + 1) % slideImages.length;
                return (
                  <div
                    key={`${item.id}-img-${index}`}
                    className={`carousel-slide absolute inset-0 ${
                      isActive ? 'active z-10' : isPrev ? 'prev z-5' : isNext ? 'next z-5' : 'hidden'
                    }`}
                    style={{
                      animation: isActive
                        ? slideDirection === 'next'
                          ? 'slideInFromRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                          : 'slideInFromLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                        : isPrev
                        ? slideDirection === 'next'
                          ? 'slideOutToLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                          : 'slideOutToRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                        : 'none'
                    }}
                  >
                    <img
                      src={imageError ? getImagePlaceholder() : (url || getImagePlaceholder())}
                      alt={item.name || 'Menu Item'}
                      loading="lazy"
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                      className={`w-full h-full object-cover transition-transform duration-200 ${
                        isExpanded ? 'scale-105' : 'group-hover:scale-105'
                      } opacity-100`}
                    />
                  </div>
                );
              })}
              {slideImages.length > 1 && (
                <>
                  <div className="absolute inset-0 flex items-center justify-between px-2 sm:px-4 z-10 pointer-events-none">
                    <button onClick={prevSlide} className="carousel-btn prev w-8 h-8 sm:w-10 sm:h-10 bg-white/90 rounded-full flex items-center justify-center text-gray-700 hover:bg-primary hover:text-white transition-all duration-300 shadow-lg pointer-events-auto">
                      <i className="fas fa-chevron-left text-sm sm:text-base"></i>
                    </button>
                    <button onClick={nextSlide} className="carousel-btn next w-8 h-8 sm:w-10 sm:h-10 bg-white/90 rounded-full flex items-center justify-center text-gray-700 hover:bg-primary hover:text-white transition-all duration-300 shadow-lg pointer-events-auto">
                      <i className="fas fa-chevron-right text-sm sm:text-base"></i>
                    </button>
                  </div>
                  <div className="absolute bottom-2 sm:bottom-3 left-1/2 transform -translate-x-1/2 flex gap-1.5 sm:gap-2 z-10">
                    {slideImages.map((_, index) => (
                      <span key={index} className={`dot w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 bg-white/50 rounded-full cursor-pointer transition-all duration-300 ${index === currentSlide ? 'active bg-primary scale-125' : ''}`} onClick={(e) => goToSlide(e, index)}></span>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <img
              src={imageError ? getImagePlaceholder() : (item.imageUrl || getImagePlaceholder())}
              alt={item.name || 'Menu Item'}
              loading="lazy"
              onLoad={handleImageLoad}
              onError={handleImageError}
              className={`w-full h-full object-cover transition-transform duration-200 ${
                isExpanded ? 'scale-105' : 'group-hover:scale-105'
              } ${imageLoaded && !imageError ? 'opacity-100' : 'opacity-0'}`}
            />
          )}
        </div>
      </div>
      
      <div className="p-2 sm:p-3 md:p-4 lg:p-4 xl:p-5 flex flex-col flex-grow">
        <div className="flex items-start justify-between mb-2 sm:mb-3">
          <h3 className="font-dm-sans font-bold text-gray-800 text-xs sm:text-sm md:text-base lg:text-base xl:text-lg 2xl:text-xl flex-1">
            {item.name || 'Untitled Item'}
          </h3>
          {item.dietaryPreference && (
            <DietaryLabel preference={item.dietaryPreference} size="xs" />
          )}
        </div>
        
        {item.hasWeightOptions && (
          <div className="mb-2 sm:mb-3 md:mb-4">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Select Weight:
            </label>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              {item.weightOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleWeightSelection(option);
                  }}
                  className={`weight-btn px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:scale-105 ${
                    selectedWeight && selectedWeight.weight === option.weight && selectedWeight.price === option.price
                      ? 'active bg-primary text-black'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {option.weight}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="pt-4 border-t border-gray-200 mt-auto">
          <div className="flex justify-between items-center">
            <span className="text-sm sm:text-base md:text-lg lg:text-lg xl:text-xl font-bold text-gray-800 price-display">
              ₹{currentPrice}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAddToCart();
              }}
              className="add-to-cart px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary to-primary-light text-gray-800 font-semibold rounded-lg hover:shadow-lg transition-all duration-200 text-xs sm:text-sm md:text-base flex items-center gap-1 sm:gap-2"
            >
              Add <i className="fas fa-plus text-xs sm:text-sm"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

MenuCard.displayName = 'MenuCard';

export default MenuCard; 